
import { Percent, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

interface PricingToggleProps {
  isYearly: boolean;
  setIsYearly: (isYearly: boolean) => void;
  monthlyPrice: number;
  yearlyPrice: number;
  savingsPercentage: number;
}

const PricingToggle = ({
  isYearly,
  setIsYearly,
  monthlyPrice,
  yearlyPrice,
  savingsPercentage
}: PricingToggleProps) => {
  const navigate = useNavigate();
  
  const handleSubscribe = () => {
    navigate('/payment', {
      state: {
        isYearly,
        amount: isYearly ? yearlyPrice : monthlyPrice
      }
    });
  };
  
  return (
    <div className="flex flex-col items-center gap-6 mb-8 bg-white rounded-xl p-8 shadow-lg border border-corporate-primary-25 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-corporate-secondary-100 via-corporate-secondary-75 to-corporate-secondary-100"></div>
      
      <div className="text-center mb-2">
        <h3 className="font-bold mb-4 text-2xl sm:text-3xl bg-gradient-to-r from-corporate-primary-100 to-corporate-primary-75 bg-clip-text text-transparent">
          Choose Annual And Save Two Months
        </h3>
        <p className="text-corporate-neutral-75 max-w-md mx-auto">
          Lock in a lower rate by switching to a yearly plan—get the same expert trading strategies at a better value!
        </p>
      </div>

      <div className="flex items-center justify-center gap-4 mb-4 bg-corporate-primary-25/50 p-3 rounded-full">
        <Label 
          htmlFor="pricing-toggle" 
          className={!isYearly ? "font-semibold text-corporate-primary-100" : "text-corporate-neutral-75"}
        >
          Monthly
        </Label>
        <Switch 
          id="pricing-toggle" 
          checked={isYearly} 
          onCheckedChange={setIsYearly} 
          className="data-[state=checked]:bg-corporate-primary-100" 
        />
        <Label 
          htmlFor="pricing-toggle" 
          className={isYearly ? "font-semibold text-corporate-primary-100" : "text-corporate-neutral-75"}
        >
          Yearly
        </Label>
      </div>

      <div className="text-5xl font-bold bg-gradient-to-br from-corporate-primary-100 to-corporate-primary-75 bg-clip-text text-transparent">
        ${isYearly ? yearlyPrice : monthlyPrice}
        <span className="text-xl text-corporate-neutral-75 ml-1">/{isYearly ? 'year' : 'month'}</span>
      </div>
      
      {isYearly && (
        <div className="flex items-center gap-2 text-green-600 bg-green-50 py-2 px-4 rounded-full">
          <Percent className="w-4 h-4" />
          <span className="font-medium">Save {savingsPercentage}% with yearly billing</span>
        </div>
      )}

      <motion.div
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.98 }}
      >
        <Button 
          onClick={handleSubscribe} 
          className="bg-gradient-to-r from-corporate-primary-100 to-corporate-primary-75 hover:from-corporate-primary-75 hover:to-corporate-primary-100 text-white mt-4 px-8 py-6 text-lg font-medium flex items-center gap-2 transition-all shadow-md hover:shadow-lg rounded-lg"
        >
          Subscribe Now
          <ArrowRight className="w-5 h-5" />
        </Button>
      </motion.div>
    </div>
  );
};

export default PricingToggle;
